import { clearAuthMode } from './auth-mode';
import type { IAuthSession, IAuthStrategy } from './auth-strategy';
import { getUserManager, startSignin, trySigninRedirectCallback } from './oidc';

/** Minimal shape of the oidc-client-ts User we read. */
interface IOidcUser {
  access_token?: string;
  expired?: boolean;
  refresh_token?: string;
  profile?: { email?: string; name?: string };
}

function sessionFrom(u: IOidcUser | null | undefined): IAuthSession | null {
  if (!u?.access_token) return null;
  // orgName is left null here; useOrgContext derives it from the Keycloak
  // `organization` claim on the token.
  return {
    token: u.access_token,
    email: u.profile?.email ?? null,
    name: u.profile?.name ?? null,
    orgName: null,
  };
}

/**
 * cortex-Keycloak sign-in strategy (the `?login=keycloak` break-glass path).
 *
 * This is the console's original `oidc-client-ts` flow, unchanged in behavior:
 * complete any redirect callback, load/refresh the user, and surface the
 * access token; `signIn` redirects to Keycloak (silent when a `kc_idp_hint`
 * is present); `signOut` navigates to Keycloak's logout endpoint.
 */
export function createKeycloakAuth(): IAuthStrategy {
  return {
    async init(onSession) {
      const mgr = getUserManager();
      const emit = (u: IOidcUser | null | undefined) => onSession(sessionFrom(u));

      const onLoaded = (u: IOidcUser) => emit(u);
      const onUnloaded = () => emit(null);
      const onSilentRenewError = (err: unknown) => {
        console.warn('console/keycloakAuth:silentRenewError', err);
        emit(null);
      };
      mgr.events.addUserLoaded(onLoaded);
      mgr.events.addUserUnloaded(onUnloaded);
      mgr.events.addSilentRenewError(onSilentRenewError);

      try {
        try {
          await trySigninRedirectCallback(mgr);
        } catch (err) {
          console.warn('OIDC redirect callback failed', err);
        }
        let u: IOidcUser | null = await mgr.getUser();
        // Cached user but token expired (tab open past the token lifetime):
        // try a refresh-grant before falling back to a full redirect.
        if (u?.expired && u.refresh_token) {
          try {
            u = await mgr.signinSilent();
          } catch (err) {
            console.warn('Silent refresh on boot failed; falling back to redirect', err);
            u = null;
            try {
              await mgr.removeUser();
            } catch (inner) {
              console.warn('console/keycloakAuth:removeUser(afterSilentFail)', inner);
            }
          }
        }
        emit(u);
      } catch (err) {
        console.error('keycloakAuth bootstrap failed', err);
        emit(null);
      }

      return () => {
        mgr.events.removeUserLoaded(onLoaded);
        mgr.events.removeUserUnloaded(onUnloaded);
        mgr.events.removeSilentRenewError(onSilentRenewError);
      };
    },

    async getConnectTokens() {
      const u: IOidcUser | null = await getUserManager().getUser();
      if (!u?.access_token) return null;
      return { accessToken: u.access_token, refreshToken: u.refresh_token ?? null };
    },

    async signIn() {
      // `kc_idp_hint` on the URL signals a deep-link from another product
      // where the user is presumed already signed into the upstream IdP —
      // attempt silent SSO first (the callback falls back to interactive).
      const fromIdpHint = new URLSearchParams(window.location.search).has('kc_idp_hint');
      await startSignin(getUserManager(), { silent: fromIdpHint });
    },

    async signOut() {
      // Break-glass ends here: clear the sticky flag so the next sign-in
      // defaults back to Coherence.
      clearAuthMode();
      // No leftovers across the provider switch: drop the Coherence refresh
      // token so a later Coherence load doesn't resurrect a stale session.
      localStorage.removeItem('cortex.formantRefreshToken');
      const authority = (
        import.meta.env.VITE_KEYCLOAK_AUTHORITY ??
        'https://keycloak.cortex-dev.formant.ai/realms/formant'
      ).replace(/\/$/, '');
      const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'cortex-console';
      const params = new URLSearchParams({
        client_id: clientId,
        post_logout_redirect_uri: `${window.location.origin}/?signed_out=1`,
      });
      window.location.assign(`${authority}/protocol/openid-connect/logout?${params.toString()}`);
    },
  };
}
